import { appLocalStorage } from "@/app/shared/services/app-storage/app-storage";
import { ApiPermissions } from "../services/volateq-api/api-permissions";
import { CustomerTokenResult } from "../services/volateq-api/api-schemas/auth-schema";
import { STORAGE_KEY_OMO_TOKEN_PREFIX, STORAGE_KEY_AUTH_TOKEN } from "../services/app-storage/storage-keys";

export interface AuthState {
  token: string;
  permissions: ApiPermissions[];
  whiteLabelingVersion?: string;
  customer?: CustomerTokenResult;
  hiddenAdmin?: boolean;
  isAdmin?: true;
  refreshToken?: string;
  /** Known after the first user lookup, so the next page load knows the user before it. */
  userId?: string;
}

/**
 * Dispatched on `window` whenever this tab updates the auth token (login,
 * refresh, logout). The browser "storage" event only fires in OTHER tabs, so
 * same-tab consumers (e.g. the realtime worker credentials push) must listen
 * for this event instead.
 */
export const AUTH_TOKEN_CHANGED_EVENT = "volateq-auth-token-changed";

export class AuthStore {
  private state: AuthState;
  private permissions: Set<ApiPermissions> = new Set();

  static getAuthState(): AuthState {
    return appLocalStorage.getItem(STORAGE_KEY_AUTH_TOKEN) || { token: "", permissions: [] };
  }

  constructor() {
    this.state = AuthStore.getAuthState();
    this.permissions = new Set(this.state.permissions);

    appLocalStorage.onChange<AuthState>(STORAGE_KEY_AUTH_TOKEN, (newAuthState) => {
      if (newAuthState) {
        this.updateStateObject(newAuthState);
      }
    });
  }

  private updateStateObject(newState: AuthState) {
    this.state.token = newState.token;
    this.state.permissions = newState.permissions;
    this.state.customer = newState.customer;
    this.state.isAdmin = newState.isAdmin;
    this.state.whiteLabelingVersion = newState.whiteLabelingVersion;
    this.state.refreshToken = newState.refreshToken;
    // A refreshed token belongs to the same user; only a logout or an expired session forgets it
    this.state.userId = newState.token ? (newState.userId ?? this.state.userId) : undefined;

    this.permissions = new Set(this.state.permissions);
  }

  public updateToken(newState: AuthState) {
    this.updateStateObject(newState);

    appLocalStorage.setItem(STORAGE_KEY_AUTH_TOKEN, this.state);

    if (!newState.token) {
      appLocalStorage.removeItemsByPrefix(STORAGE_KEY_OMO_TOKEN_PREFIX);
    }

    window.dispatchEvent(new Event(AUTH_TOKEN_CHANGED_EVENT));
  }

  public updateCustomer(customer: CustomerTokenResult | undefined) {
    this.state.customer = customer;

    appLocalStorage.setItem(STORAGE_KEY_AUTH_TOKEN, this.state);
  }

  public updateUserId(userId: string) {
    if (this.state.userId === userId) {
      return;
    }

    this.state.userId = userId;

    appLocalStorage.setItem(STORAGE_KEY_AUTH_TOKEN, this.state);
  }

  public updateHidden(isHidden: boolean) {
    if (!this.state.isAdmin) {
      return;
    }

    this.state.hiddenAdmin = isHidden;

    appLocalStorage.setItem(STORAGE_KEY_AUTH_TOKEN, this.state);
  }

  public get isAuthenticated(): boolean {
    return !!this.state.token;
  }

  public get isVolateqAdmin(): boolean {
    return !!this.state.isAdmin;
  }

  public get isHiddenAdmin(): boolean {
    return !!this.state.hiddenAdmin;
  }

  public get customer(): CustomerTokenResult | undefined {
    return this.state.customer;
  }

  public get token(): string {
    return this.state.token;
  }

  public get whiteLabelingVersion(): string | undefined {
    return this.state.whiteLabelingVersion;
  }

  public get userId(): string | undefined {
    return this.state.userId;
  }

  public get refreshToken(): string | undefined {
    return this.state.refreshToken;
  }

  public hasPermission(...perms: ApiPermissions[]): boolean {
    for (const p of perms) {
      if (!this.permissions.has(p)) {
        return false;
      }
    }

    return true;
  }
}

export const authStore = new AuthStore();
